import each from 'lodash-es/each';
import pick from 'lodash-es/pick';
import find from 'lodash-es/find';
import findIndex from 'lodash-es/findIndex';
import filter from 'lodash-es/filter';
import remove from 'lodash-es/remove';
import omitBy from 'lodash-es/omitBy';
import merge from 'lodash-es/merge';
import defaultsDeep from 'lodash-es/defaultsDeep';

import isFunction from 'lodash-es/isFunction';
import isString from 'lodash-es/isString';
import updateStyle from '../css/updateStyle';
import frameProps from '../css/frameProps';
import defaults from './grid.defaults';
import {renderControls} from '../renderControls';
import uniqueId from '../uniqueId';
import getDisabledStyle from '../css/disabledStyle';
import Control from './control';
import lookAndFeel from '../lookAndFeel';
import updateStyleByRef from '../css/updateStyleByRef';

const extraEl = [
  'styleSheet',
  'styleSheetRow',
  'styleSheetDeepRow',
  'styleSheetColumn',
];

export default class Grid extends Control {

  getExtraEl(){
    return extraEl;
  }

  constructor(instance, path, nicePath) {
    super(instance, path, nicePath);    
    this._ignoreData = true;
    this.postValue = (result) => {
      if (this.options.selectionMode === 'all') {
        if (this.options.idForceUnique) {
          return this.data.map((row) => row.id);
        } else {
          return this.data.map((row, i) => (row.index !== undefined ? row.index : i));
        }
      }

      return result;
    };
  }

  onReady(){
    super.onReady();
    this.addTrigger('viewMode',()=>{
      delete this.dataHash;
    });
  }

  updateDataSourceParameter() {
    // console.log('updateDataSourceParameter');
    super.updateDataSourceParameter();
    const originalOptions = this._controlOriginalOptions;
    each(originalOptions.columns, (c) => {
      if (
        c.control &&
        c.control.value &&
        c.control.value.type == 'parameterBinding' &&
        this.instance.datasourceParameters[c.control.value.name] === undefined
      ) {
        this.instance.datasourceParameters[c.control.value.name] = [];
      }
    });
    // console.log('grid updateDataSourceParameter');
    if (originalOptions.data) {
      const cells =
        (originalOptions.data[1] &&
          originalOptions.data[1].type === 'model' &&
          originalOptions.data[1].cells) ||
        (originalOptions.data[1] && originalOptions.data[1].cells);

      each(cells, (cell, cellIndex) => {
        each(cell, (value, field) => {
          if (value && value.value && value.value.type == 'parameterBinding') {
            this.instance.datasourceParameters[value.value.name] = this.data.map(
              (row) => row.cells[cellIndex] && row.cells[cellIndex][field]
            );
          }
        });
      });
    }
  }

  get data() {
    if (this._data) {
      return [...this._data];
    }

    if (this._controlOptions.persistent) {
      const key = 'recharts.persistent.' + this.instance.args.cid + ':' + this.path + '.data';
      const value = localStorage.getItem(key);
      if (value !== null) {
        return JSON.parse(value);
      }
    }

    return this._controlOptions.data&&[...this._controlOptions.data]||[];
  }

  set data(data) {
    delete this._pkIndex;
    delete this.selectionHash;
    this._data = [...data];
    this._ensureUnique();
    if (this._controlOptions.persistent) {
      const key = 'recharts.persistent.' + this.instance.args.cid + ':' + this.path + '.data';
      localStorage.setItem(key, JSON.stringify(this._data));
    }
  }

  offlineData() {
    if (!this._data) {
      this.data = this.data;
    }
  }

  _cleanRow(row) {
    return {
      ...row,
      cells: row.cells.map((cell) => {
        const { generated, type, ...c } = cell;
        return c;
      }),
    };
  }

  addRow(row) {
    this.offlineData();
    if (this._ensureUniqueId(row.id)) {
      row = this._cleanRow(row);
      if (this._pkIndex) {
        this._pkIndex[row.id] = row;
      }

      this._data.push(row);
      if (this._controlOptions.persistent) {
        const key = 'recharts.persistent.' + this.instance.args.cid + ':' + this.path + '.data';
        localStorage.setItem(key, JSON.stringify(this._data));
      }

      return true;
    }

    return false;
  }

  insertRow(row, index = 0) {
    this.offlineData();
    if (this._ensureUniqueId(row.id)) {
      row = this._cleanRow(row);
      if (this._pkIndex) {
        this._pkIndex[row.id] = row;
      }

      this._data.splice(index, 0, row);
      if (this._controlOptions.persistent) {
        const key = 'recharts.persistent.' + this.instance.args.cid + ':' + this.path + '.data';
        localStorage.setItem(key, JSON.stringify(this._data));
      }

      return true;
    }

    return false;
  }

  removeRow(id) {
    this.offlineData();
    remove(this._data, { id });
    if (this._pkIndex && !this._ensureUniqueId(id)) {
      delete this._pkIndex[id];
    }

    if (this._controlOptions.persistent) {
      const key = 'recharts.persistent.' + this.instance.args.cid + ':' + this.path + '.data';
      localStorage.setItem(key, JSON.stringify(this._data));
    }
  }

  removeRows(column, value) {
    if (isString(column)) {
      column = findIndex(this.options.columns, { header: column });
      if (column === undefined) {
        throw Error(`Column "${column}" not found in the grid "${this.options.name}"`);
      }
    }

    this.offlineData();
    remove(this._data, [['cells', column], value]);
    if (this._pkIndex) {
      this._pkIndex = omitBy(this._pkIndex, [['cells', column], value]);
    }

    if (this._controlOptions.persistent) {
      const key = 'recharts.persistent.' + this.instance.args.cid + ':' + this.path + '.data';
      localStorage.setItem(key, JSON.stringify(this._data));
    }
  }

  removeRowAt(index = 0) {
    this.offlineData();
    if (this._pkIndex) {
      const id = this._data[index].id;
      if (!this._ensureUniqueId(id)) {
        delete this._pkIndex[id];
      }
    }

    this._data.splice(index, 1);
    if (this._controlOptions.persistent) {
      const key = 'recharts.persistent.' + this.instance.args.cid + ':' + this.path + '.data';
      localStorage.setItem(key, JSON.stringify(this._data));
    }
  }

  getRow(id) {
    if (this.options.idForceUnique) {
      return this._pkIndex[id];
    }

    return find(this.data, { id });
  }

  findRow(column, value) {
    if (isString(column)) {
      column = findIndex(this.options.columns, { header: column });
      if (column === undefined) {
        throw Error(`Column "${column}" not found in the grid "${this.options.name}"`);
      }
    }

    return find(this.data, [['cells', column], value]);
  }

  findRows(column, value) {
    if (isString(column)) {
      column = findIndex(this.options.columns, { header: column });
      if (column === undefined) {
        throw Error(`Column "${column}" not found in the grid "${this.options.name}"`);
      }
    }

    return filter(this.data, [['cells', column], value]);
  }

  clearRows() {
    if (!this._data) {
      this._data = [];
    } else {
      this._data.length = 0;
    }

    delete this._pkIndex;
    this._ensureUnique();
    if (this._controlOptions.persistent) {
      const key = 'recharts.persistent.' + this.instance.args.cid + ':' + this.path + '.data';
      localStorage.setItem(key, JSON.stringify(this._data));
    }
  }

  resetData() {
    delete this._data;
    delete this._pkIndex;
    this._ensureUnique();
    if (this._controlOptions.persistent) {
      const key = 'recharts.persistent.' + this.instance.args.cid + ':' + this.path + '.data';
      localStorage.setItem(key, null);
    }
  }

  _ensureUnique(force) {
    // console.time('grid._ensureUnique');
    if (this.options.idForceUnique && (force || !this._pkIndex)) {
      this._pkIndex = {};

      const data = filter(this.data, (row) => {
        if (!this._pkIndex[row.id]) {
          this._pkIndex[row.id] = row;
          return true;
        }

        return false;
      });
      if (!this._data) {
        this._controlOptions.data = data;
      } else {
        this._data = data;
      }
    }

    // console.timeEnd('grid._ensureUnique');
  }

  _ensureUniqueId(id) {
    this._ensureUnique();
    return !this.options.idForceUnique || !this._pkIndex[id];
  }

  getSelectedRows() {
    // console.time('grid.getSelectedRows');
    try {
      if (!this.options.selectionMode || this.options.selectionMode == 'none') {
        return [];
      }

      if (this.options.selectionMode == 'all') {
        return [...this.data];
      }

      const value = this.value || [];
      if (this.options.idForceUnique) {
        return filter(this.data, (row) => {
          return value.indexOf(row.id) !== -1;
        });
      } else {
        return filter(this.data, (row, i) => {
          return value.indexOf(i) !== -1;
        });
      }
    } finally {
      // console.timeEnd('grid.getSelectedRows');
    }
  }

  setSelectedRows(rows) {
    // console.time('grid.setSelectedRows');
    if (!this.options.selectionMode || this.options.selectionMode == 'none') {
      return;
    }

    if (this.options.selectionMode === 'all') {
      throw new Error('Selection mode "all" - cannot set selection, it\'s always all the rescords');
    }

    if (this.options.idForceUnique) {
      this.value = (rows || []).map((row) => row.id);
    } else {
      this.value = (rows || [])
        .map((row) => (row.index !== undefined ? row.index : this.data.indexOf(row)))
        .filter((i) => i !== -1);
    }

    // console.timeEnd('grid.setSelectedRows');
  }

  setSelectedRowIndex(index) {
    if (!this.options.selectionMode || this.options.selectionMode == 'none') {
      return;
    }

    if (this.options.selectionMode === 'all') {
      throw new Error('Selection mode "all" - cannot set selection, it\'s always all the records');
    }

    if (typeof index !== 'number' || index < 0 || index >= this.data.length) {
      throw new Error(`Invalid row index: ${index}. Must be between 0 and ${this.data.length - 1}`);
    }

    if (this.options.idForceUnique) {
      const row = this.data[index];
      this.value = row ? [row.id] : [];
    } else {
      this.value = [index];
    }
  }

  setSelectedRowsIndex(indexes) {
    if (!this.options.selectionMode || this.options.selectionMode == 'none') {
      return;
    }

    if (this.options.selectionMode === 'all') {
      throw new Error('Selection mode "all" - cannot set selection, it\'s always all the records');
    }

    if (!Array.isArray(indexes)) {
      throw new Error('Indexes must be an array');
    }

    const validIndexes = indexes.filter((index) => {
      return typeof index === 'number' && index >= 0 && index < this.data.length;
    });

    if (this.options.idForceUnique) {
      this.value = validIndexes.map((index) => this.data[index].id).filter((id) => id !== undefined);
    } else {
      this.value = validIndexes;
    }
  }

  setSelectedRowId(id) {
    if (!this.options.selectionMode || this.options.selectionMode == 'none') {
      return;
    }

    if (this.options.selectionMode === 'all') {
      throw new Error('Selection mode "all" - cannot set selection, it\'s always all the records');
    }

    const oldValue = this.value ? this.value.slice() : [];

    if (id === undefined || id === null) {
      this.value = [];
    } else if (this.options.idForceUnique) {
      if (this.options.selectionMode === 'single') {
        this.value = [id];
      } else {
        const value = this.value || [];
        if (value.indexOf(id) === -1) {
          value.push(id);
          this.value = value;
        }
      }
    } else {
      const index = findIndex(this.data, (row) => row.id == id);
      if (index !== -1) {
        if (this.options.selectionMode === 'single') {
          this.value = [index];
        } else {
          const value = this.value || [];
          if (value.indexOf(index) === -1) {
            value.push(index);
            this.value = value;
          }
        }
      }
    }

    this.render();
    this._fireRowSelect(oldValue);
    if (id !== undefined && id !== null) {
      this.scrollToRowId(id);
    }
  }

  setSelectedRowIds(ids) {
    if (!this.options.selectionMode || this.options.selectionMode == 'none') {
      return;
    }

    if (this.options.selectionMode === 'all') {
      throw new Error('Selection mode "all" - cannot set selection, it\'s always all the records');
    }

    if (!Array.isArray(ids)) {
      throw new Error('ids must be an array');
    }

    const oldValue = this.value ? this.value.slice() : [];

    if (this.options.idForceUnique) {
      this.value = ids;
    } else {
      this.value = ids
        .map((id) => findIndex(this.data, (row) => row.id == id))
        .filter((i) => i !== -1);
    }

    this.render();
    this._fireRowSelect(oldValue);
    if (ids && ids.length) {
      this.scrollToRowId(ids[0]);
    }
  }

  clearSelection() {
    const oldValue = this.value ? this.value.slice() : [];
    this.value = [];
    this.render();
    this._fireRowSelect(oldValue);
  }

  _getHeaderOffset() {
    if (this.options.staticHeader === false || !this.el) { return 0; }
    const headerEl = this.el.querySelector('.body table tr.header');
    return headerEl ? headerEl.offsetHeight : 0;
  }

  scrollToRowId(id) {
    if (!this._rowElements || !this.el) { return; }
    // Direct hit on the id map — fast path, preserves backwards compatible
    // exact-match behaviour for IDs that happen to contain no '*'.
    let rowEl = this._rowElements.id[id];

    // Wildcard fallback. Only kicks in when the id is a string containing
    // '*' AND the exact lookup missed — so an id literally equal to "*1*"
    // (unlikely but possible) still wins the exact match. `*` matches any
    // character sequence including empty; all other regex metacharacters
    // are escaped so they match literally. First insertion-order match
    // wins (consistent with how Object.keys iterates the row id map).
    //   "*1*"     → contains "1"
    //   "1*"      → starts with "1"
    //   "Rob*"    → starts with "Rob"
    //   "*mitry"  → ends with "mitry"
    if (!rowEl && typeof id === 'string' && id.indexOf('*') !== -1) {
      const escaped = id.replace(/[.+?^${}()|[\]\\]/g, '\\$&');
      const pattern = new RegExp('^' + escaped.replace(/\*/g, '.*') + '$');
      const ids = Object.keys(this._rowElements.id);
      for (let i = 0; i < ids.length; i++) {
        if (pattern.test(ids[i])) {
          rowEl = this._rowElements.id[ids[i]];
          break;
        }
      }
    }

    if (!rowEl) { return; }
    const bodyEl = this.el.querySelector('.body');
    if (!bodyEl) { return; }
    const bodyRect = bodyEl.getBoundingClientRect();
    const rowRect = rowEl.getBoundingClientRect();
    const headerOffset = this._getHeaderOffset();
    if (rowRect.top < bodyRect.top + headerOffset || rowRect.bottom > bodyRect.bottom) {
      bodyEl.scrollTop += rowRect.top - bodyRect.top - headerOffset;
    }
  }

  scrollToRowIndex(index) {
    if (!this._rowElements || !this.el) { return; }
    const rowEl = this._rowElements.index[index + 1];
    if (!rowEl) { return; }
    const bodyEl = this.el.querySelector('.body');
    if (!bodyEl) { return; }
    const bodyRect = bodyEl.getBoundingClientRect();
    const rowRect = rowEl.getBoundingClientRect();
    const headerOffset = this._getHeaderOffset();
    if (rowRect.top < bodyRect.top + headerOffset || rowRect.bottom > bodyRect.bottom) {
      bodyEl.scrollTop += rowRect.top - bodyRect.top - headerOffset;
    }
  }

  _fireRowSelect(oldValue) {
    const control = this;
    const newValue = JSON.stringify(control.value || []);
    if (JSON.stringify(oldValue || []) === newValue) {
      return;
    }

    const rowSelect =
      control._controlOptions.triggers && control._controlOptions.triggers.rowSelect;
    if (rowSelect) {
      if (!isFunction(rowSelect)) {
        console.error("grid's rowSelect trigger should be a function", rowSelect);
      } else {
        const selectedRows = control
          .getSelectedRows()
          .map((r) => ({ ...r, index: control.data.indexOf(r) }));

        setTimeout(() => {
          rowSelect.apply(null, [selectedRows, control, control.instance, control.path, null]);
        }, 0);
      }
    }
  }

  renderElement() {
    // console.time('grid render');
    if (!this.value) {
      this.value = [];
    }

    this._ensureUnique(true);
    const control = this;
    const options = this._controlOptions;
    const originalOptions = this._controlOriginalOptions;
    originalOptions.columns = _.compact(originalOptions.columns);
    const instance = this.instance;
    const path = this.path;
    // console.time('grid render aplly defaults');
    var gridOptions = defaultsDeep(options, defaults.options);
    // console.timeEnd('grid render aplly defaults');

    if (!control.el) {
      // console.time('grid render create element');
      control.el = defaults.createElement(instance);
      if(!control.id){
        control.id = uniqueId(control.el, 'recharts-grid-',Grid);
        control.styleSheet = 'css-' + control.id;
        control.styleSheetRow = 'css-row-' + control.id;
        control.styleSheetDeepRow = 'css-deep-row-' + control.id;
        control.styleSheetColumn = 'css-column-' + control.id;        
      }else{
        control.el.setAttribute('id',control.id);
      }

      // delete renderGrid.options;
      if (!control.isLayoutMode) {

        const handler = function (ev) {
          // console.log('gridclick',ev);
          if(ev.type=='mouseup' && ev.button !== 2){
            return;
          }

          let gridSubEl = ev.target;
          while (
            gridSubEl.dataset.rowId === undefined &&
            gridSubEl !== tableEl &&
            gridSubEl.parentElement
          ) {
            gridSubEl = gridSubEl.parentElement;
          }

          const gridElement = {
            rowId: gridSubEl.dataset.rowId,
            rowIndex: gridSubEl.dataset.rowIndex,
            cellIndex: gridSubEl.dataset.cellIndex,
            HTMLElement: gridSubEl,
          };          

          if (gridElement.rowIndex !== 'header' && gridElement.rowIndex !== undefined) {
            gridElement.rowIndex = Number(gridElement.rowIndex);
          }

          if (gridElement.cellIndex !== undefined) {
            gridElement.cellIndex = Number(gridElement.cellIndex);
          }

          if (gridElement.rowIndex !== 'header') {
            gridElement.record = gridElement.rowId ? control.getRow(gridElement.rowId): control.data[gridElement.rowIndex];
          }

          control.anchor = gridElement;

          if(ev.button == 2){
            return;
          }

          let selectionChanged = false;

          if (
            control.options.selectionMode &&
            control.options.selectionMode !== 'none' &&
            control.options.selectionMode !== 'all'
          ) {
            // console.time('grid update value');
            let value = control.value || [];
            if (control.options.idForceUnique && gridElement.rowId !== undefined) {
              selectionChanged = true;
              if (control.options.selectionMode == 'single') {
                if (value[0] !== gridElement.rowId) {
                  control.value = [gridElement.rowId];
                } else {
                  control.value = [];
                }
              } else {
                if (value.indexOf(gridElement.rowId) !== -1) {
                  control.value = value.filter(function(id) {
                    return id != gridElement.rowId;
                  });
                } else {
                  value.push(gridElement.rowId);
                  control.value = value;
                }
              }
            } else if (!control.options.idForceUnique && gridElement.rowIndex !== undefined) {
              selectionChanged = true;
              if (control.options.selectionMode == 'single') {
                if (value[0] !== gridElement.rowIndex) {
                  control.value = [gridElement.rowIndex];
                } else {
                  control.value = [];
                }
              } else {
                if (value.indexOf(gridElement.rowIndex) !== -1) {
                  control.value = value.filter(function(ind) {
                    return ind != gridElement.rowIndex;
                  });
                } else {
                  value.push(gridElement.rowIndex);
                  control.value = value;
                }
              }
            }

            // console.timeEnd('grid update value');
            if (selectionChanged) {
              // console.time('grid render due to selection');
              control.render();
              // console.timeEnd('grid render due to selection');
            }
          }

          setTimeout(() => {
            if (control._controlOptions.trigger) {
              if (!isFunction(control._controlOptions.trigger)) {
                console.error(
                  "grid's click trigger should be a function",
                  control._controlOptions.trigger
                );
              } else {
                control._controlOptions.trigger.apply(this, [
                  merge({}, control._controlOptions, { gridElement, grid: control }),
                  instance,
                  control,
                  path,
                  ev,
                ]);
              }
            }

            const cellClick =
              control._controlOptions.triggers && control._controlOptions.triggers.cellClick;
            if (gridElement.cellIndex !== undefined && cellClick) {
              if (!isFunction(cellClick)) {
                console.error("grid's cellClick trigger should be a function", cellClick);
              } else {
                // Header cells have cellIndex but no rowIndex; pass null for
                // row/cell.data instead of dereferencing undefined.
                const dataRow = gridElement.rowIndex !== undefined
                  ? control.data[gridElement.rowIndex]
                  : null;
                const cell = {
                  index: gridElement.cellIndex,
                  data: (dataRow && dataRow.cells)
                    ? dataRow.cells[gridElement.cellIndex]
                    : null,
                };
                if (cell.data) {
                  cell.value = cell.data.value;
                }

                const row = dataRow
                  ? Object.assign({}, dataRow, { index: gridElement.rowIndex })
                  : null;
                try {
                  cellClick.apply(this, [cell, row, control, instance, path, ev]);
                } catch (exc) {
                  const _ctrlName = (control._controlOriginalOptions && control._controlOriginalOptions.name) || '';
                  console.error(`[grid trigger] Event "cellClick" failed [control: "${_ctrlName}", type: "grid", path: "${control.path || ''}"]`, exc);
                }
              }
            }

            const rowClick =
              control._controlOptions.triggers && control._controlOptions.triggers.rowClick;
            if (gridElement.rowIndex !== undefined && rowClick) {
              if (!isFunction(rowClick)) {
                console.error("grid's rowClick trigger should be a function", rowClick);
              } else {
                //row, grid, component, path, event
                const row = {
                  ...control.data[gridElement.rowIndex],
                  index: gridElement.rowIndex,
                };
                try {
                  rowClick.apply(this, [row, control, instance, path, ev]);
                } catch (exc) {
                  const _ctrlName = (control._controlOriginalOptions && control._controlOriginalOptions.name) || '';
                  console.error(`[grid trigger] Event "rowClick" failed [control: "${_ctrlName}", type: "grid", path: "${control.path || ''}"]`, exc);
                }
              }
            }

            if (selectionChanged) {
              const rowSelect =
                control._controlOptions.triggers && control._controlOptions.triggers.rowSelect;
              if (gridElement.rowIndex !== undefined && rowSelect) {
                if (!isFunction(rowSelect)) {
                  console.error("grid's rowSelect trigger should be a function", rowSelect);
                } else {
                  //selectedRows, grid, component, path, event
                  const selectedRows = control
                    .getSelectedRows()
                    .map((r) => ({ ...r, index: control.data.indexOf(r) }));

                  try {
                    rowSelect.apply(this, [selectedRows, control, instance, path, ev]);
                  } catch (exc) {
                    const _ctrlName = (control._controlOriginalOptions && control._controlOriginalOptions.name) || '';
                    console.error(`[grid trigger] Event "rowSelect" failed [control: "${_ctrlName}", type: "grid", path: "${control.path || ''}"]`, exc);
                  }
                }
              }
            }

            if (control._controlOptions.event) {
              if (!isString(control._controlOptions.event)) {
                console.error(
                  "grid's click event should be a string",
                  control._controlOptions.event
                );
              } else {
                instance.fire(
                  control._controlOptions.event,
                  merge(
                    {},
                    pick(control._controlOptions, [
                      'name',
                      'value.defaultValue',
                      'type',
                      'label',
                      'toggleGroup',
                      'rowId',
                      'rowIndex',
                      'cellIndex',
                    ]),
                    { gridElement, grid: control }
                  ),
                  control,
                  path,
                  ev
                );
              }
            }
          }, 0);
          // console.timeEnd('grid click');
        }

        control.el.addEventListener('dblclick', handler);
        control.el.addEventListener('click', handler);
        control.el.addEventListener('mouseup', handler);

        // Fallback: in some environments (e.g. grid inside toolbar), the browser
        // does not generate a click event after mousedown+mouseup on table elements.
        // Detect this and dispatch a synthetic click on the correct row.
        let _clickFired = false;
        control.el.addEventListener('click', function() { _clickFired = true; }, true);
        control.el.addEventListener('mousedown', function(ev) {
          if (ev.button !== 0) { return; }
          _clickFired = false;
          const clientX = ev.clientX;
          const clientY = ev.clientY;
          setTimeout(function() {
            if (!_clickFired) {
              const el = document.elementFromPoint(clientX, clientY);
              if (el && control.el.contains(el)) {
                let tr = el;
                while (tr && tr.tagName !== 'TR' && tr !== tableEl) {
                  tr = tr.parentElement;
                }

                if (tr && tr.tagName === 'TR') {
                  tr.click();
                }
              }
            }
          }, 150);
        });
      }

      // console.timeEnd('grid render create element');
    }

    var bodyEl = control.el.querySelector('.body');
    var tableEl = bodyEl.querySelector('table');
    var emptyEl = bodyEl.querySelector('.empty-label');
    emptyEl.innerHTML = control.error || gridOptions.emptyLabel.text || '';

    if (gridOptions.staticHeader !== false) {
      control.el.classList.add('static-header');
    } else {
      control.el.classList.remove('static-header');
    }

    if (gridOptions.resizableColumns) {
      control.el.classList.add('resizable-columns');
    } else {
      control.el.classList.remove('resizable-columns');
    }

    // console.time('grid render prepare styles');
    const cssStyle = defaults.cssStyle(instance);

    // const strOptions = JSON.stringify(gridOptions);
    // if (renderGrid.options === strOptions) {
    //   return control.el;
    // }

    // renderGrid.options = strOptions;

    defaults.updateGridClasses(control, gridOptions);
    defaults.updateRowClasses(control, gridOptions);
    //renderGrid.options = strOptions;

    var stylePatch = {};
    merge(stylePatch, cssStyle.normal);

    if (gridOptions.style && gridOptions.style.normal) {
      merge(stylePatch, pick(gridOptions.style.normal, frameProps.concat('overflow')));
    }
    
    // if (control.isHover) {
    //   merge(stylePatch, cssStyle.hover);
    // }

    // if (control.isDragOver) {
    //   merge(stylePatch, cssStyle.dragOver);
    // }

    merge(stylePatch, control.frameStyle);

    if (control.disabled) {
      merge(stylePatch, getDisabledStyle());
      if (options.style && options.style.disabled) {
        merge(stylePatch, options.style.disabled);
      }

      if (control.isLayoutMode) {
        delete stylePatch.pointerEvents;
      }
    }

    if (instance.options.chartSize && gridOptions.layout && gridOptions.layout != 'custom') {
      const [x, y] = gridOptions.layout.split(',');
      const [w, h] = instance.options.chartSize.split('x');
      const i = (x - 1) * h + (y - 1) * 1;
      const { top, right, bottom, left } = instance.options.grid[i];
      merge(stylePatch, {
        top,
        right,
        bottom,
        left,
        width: 'auto',
        height: 'auto',
      });
    }

    // console.timeEnd('grid render prepare styles');
    // console.time('grid render render styles');
    let scheme = lookAndFeel(options.lookAndFeel, stylePatch.height);
    updateStyleByRef(stylePatch, scheme);
    updateStyle(control.el, stylePatch, control.currentStyle);
    control.currentStyle = stylePatch;
    // console.timeEnd('grid render render styles');

    if (!gridOptions.columns) {
      gridOptions.columns = [];
    }

    gridOptions.columns = _.compact(gridOptions.columns);

    // if (!control.data) {
    //   control.data = [];
    // }

    var columnsHash = funJSON.stringify(gridOptions.columns);
    if (control.columnsHash !== columnsHash) {
      control.sortColumns = [];
      // console.time('grid render column headers');
      control.columnsHash = columnsHash;
      delete control.dataHash;
      let headerRow = tableEl.querySelector('tr.header');

      if (!headerRow) {
        headerRow = tableEl.insertBefore(document.createElement('tr'), tableEl.firstChild);
        headerRow.classList.add('header');
        headerRow.dataset.rowIndex = 'header';
        headerRow.dataset.rowId = 'header';
      }

      defaults.updateGridColumnClasses(control, gridOptions.columns);
      // User-resized widths are stored on control._columnWidths (px numbers
      // keyed by column index). Reset whenever the columns array changes
      // shape so a column removal doesn't carry over a stale width.
      if (!control._columnWidths || control._columnWidths.length !== gridOptions.columns.length) {
        control._columnWidths = new Array(gridOptions.columns.length);
      }

      const _gridDefaultMinColW = gridOptions.minColumnWidth !== undefined ? gridOptions.minColumnWidth : 40;
      gridOptions.columns.forEach((col, i) => {
        if (!headerRow.children[i]) {
          headerRow.appendChild(document.createElement('th'));
        }

        const thEl = headerRow.children[i];
        thEl.innerHTML = col.header || '';
        thEl.dataset.rowIndex = 'header';
        thEl.dataset.cellIndex = i;
        thEl.dataset.rowId = 'header';
        control.sortColumns[i] = col.sort || 'none';

        // Apply any user-dragged width inline so it survives the next render
        // pass. (Static widths from column.style.normal.width are handled by
        // updateGridColumnClasses via the stylesheet.)
        if (control._columnWidths[i] != null) {
          thEl.style.width = control._columnWidths[i] + 'px';
        } else {
          thEl.style.width = '';
        }

        // Resize handle. Opt-in via gridOptions.resizableColumns; opt-out per
        // column via col.resizable === false.
        const colResizable = gridOptions.resizableColumns && col.resizable !== false;
        let handle = thEl.querySelector(':scope > .qos-col-resize');
        if (colResizable) {
          if (!handle) {
            handle = document.createElement('div');
            handle.className = 'qos-col-resize';
            thEl.appendChild(handle);

            // Drag wiring. mousedown captures starting cursor X and TH width;
            // mousemove updates the TH width clamped to col.minWidth (or the
            // grid-level minColumnWidth default); mouseup commits and tears
            // down the listeners. Bound to document so the drag continues
            // even if the cursor leaves the small handle el. preventDefault
            // + stopPropagation on the mousedown so the click-to-sort
            // handler doesn't fire on a drag.
            handle.addEventListener('mousedown', function(ev) {
              ev.preventDefault();
              ev.stopPropagation();

              const colIdx = parseInt(thEl.dataset.cellIndex, 10);
              const colDef = gridOptions.columns[colIdx];
              const colMin = colDef && colDef.minWidth != null ? parseFloat(colDef.minWidth) : _gridDefaultMinColW;
              const startX = ev.clientX;
              const startWidth = thEl.getBoundingClientRect().width;

              control.el.classList.add('col-resizing');

              const onMove = function(mv) {
                let next = startWidth + (mv.clientX - startX);
                if (next < colMin) { next = colMin; }
                thEl.style.width = next + 'px';
                control._columnWidths[colIdx] = next;
              };

              const onUp = function() {
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
                control.el.classList.remove('col-resizing');
              };

              document.addEventListener('mousemove', onMove);
              document.addEventListener('mouseup', onUp);
            });
          }
        } else if (handle) {
          handle.remove();
        }
      });
      if (gridOptions.columns.length < headerRow.children.length) {
        for (var i = gridOptions.columns.length; i < headerRow.children.length; i++) {
          headerRow.children[i].remove();
        }
      }

      // console.timeEnd('grid render column headers');
    }

    // console.time('grid render data hash');
    let data = control.data;

    let dataHash = funJSON.stringify([data,options.lookAndFeel,stylePatch]);
    // console.timeEnd('grid render data hash');

    if (control.dataHash !== dataHash) {
      // console.time('grid render rows');
      

      let mut = false;
      if (control.sortColumns.length) {
        const sortFields = [];
        const sortDirections = [];
        control.sortColumns.forEach((by, i) => {
          if (by !== 'none') {
            sortFields.push(`cells[${i}].text`);
            sortDirections.push(by);
          }
        });

        data = _.orderBy(data, sortFields, sortDirections);
        mut=true;
      }

      if(control.viewMode=='tree'){
        let treeData = [];
        let parents=[{
          id: this.baseId||'',
          level:-1
        }];

        let total=0;
        while(parents.length){
          if(total>10000){break;}

          for(let i=0;i<data.length;i++){
            total++;
            if(total>10000){break;}

            const row = data[i];
            const parent = parents[parents.length-1];
            if(row.level===undefined&&(!row.parentId||row.parentId==parent.id)){
              row.level = parent.level+1;
              treeData.push(row);
              parents.push(row);
              i=0;
              continue;
            }
          }

          parents.pop();
        }

        data = treeData;
        mut=true;
      }

      if(mut){
        if(control._data){
          control._data = data;
        }else{
          control._controlOptions.data = data;
        }
      }

      control.dataHash = funJSON.stringify([data,options.lookAndFeel,stylePatch]);


      control._rowElements = { index: {}, key: {}, id: {} };
      const startIndex = 1;
      //tableEl.childNodes.length && tableEl.childNodes[0].classList.contains('header') ? 1 : 0;

      options.controls = {};
      if (!control.controls) {
        control.controls = {};
      }

      if (!this.options.idForceUnique) {
        while (tableEl.childNodes[startIndex]) {
          tableEl.childNodes[startIndex].remove();
        }
      }

      let controlIndex = 0;

      if (
        !options.showSampleRow &&
        data[0] &&
        data[0].cells &&
        data[0].cells[0] &&
        data[0].cells[0].generated
      ) {
        control.data = [];
        data = [];
      }

      if(!data.length){
        emptyEl.style.display = '';
      }else{
        emptyEl.style.display = 'none';
      }

      //console.log('grid data:', data);
      const idForceUnique = control.options.idForceUnique;
      data.forEach((row, rowIndex) => {
        let key = this.options.idForceUnique ? row.id : rowIndex;
        if (!tableEl.childNodes[rowIndex + startIndex]) {
          tableEl.appendChild(document.createElement('tr'));
        } else {
          const nodes = Array.from(tableEl.childNodes.values());
          const nodeIndex = nodes.findIndex((n) => n.dataset.rowId === row.id);

          if (nodeIndex === -1) {
            tableEl.insertBefore(
              document.createElement('tr'),
              tableEl.childNodes[rowIndex + startIndex]
            );
          } else if (nodeIndex !== rowIndex + startIndex) {
            tableEl.insertBefore(
              tableEl.childNodes[nodeIndex],
              tableEl.childNodes[rowIndex + startIndex]
            );
          }
        }

        const rowEl = tableEl.childNodes[rowIndex + startIndex];
        rowEl.dataset.rowIndex = rowIndex;
        rowEl.dataset.rowKey = key;
        rowEl.dataset.rowId = row.id;
        control._rowElements.index[rowIndex + startIndex] = rowEl;
        control._rowElements.key[key] = rowEl;
        control._rowElements.id[row.id] = rowEl;

        if (idForceUnique) {
          if (control.value.indexOf(row.id) == -1) {
            rowEl.classList.remove('selected');
          } else {
            rowEl.classList.add('selected');
          }
        } else {
          if (control.value.indexOf(rowIndex) == -1) {
            rowEl.classList.remove('selected');
          } else {
            rowEl.classList.add('selected');
          }
        }

        gridOptions.columns.forEach((column, cellIndex) => {
          if (!rowEl.childNodes[cellIndex]) {
            rowEl.appendChild(document.createElement('td'));
          }

          const tdEl = rowEl.childNodes[cellIndex];
          tdEl.dataset.rowIndex = rowIndex;
          tdEl.dataset.rowKey = key;
          tdEl.dataset.cellIndex = cellIndex;

          tdEl.dataset.rowId = row.id;          
          // tdEl.innerHTML = '';

          let cell = (row && row.cells && row.cells[cellIndex]) || {};
          let ctrl = _.merge({}, column.control);
          if(!ctrl.type){
            ctrl.type = column.type.replace(/Column$/, '');            
          }

          _.set(ctrl, 'style.normal.position', 'relative');
          if (!_.has(ctrl, 'style.normal.width')) {
            _.set(ctrl, 'style.normal.width', 'auto');
          }

          if (!_.has(ctrl, 'style.normal.top')) {
            _.set(ctrl, 'style.normal.top', '0px');
          }

          if (!_.has(ctrl, 'style.normal.left')) {
            _.set(ctrl, 'style.normal.left', '0px');
          }         

          if(!cellIndex && control.viewMode=='tree' && row.level){            
            _.set(ctrl, 'style.normal.marginLeft', (row.level*16)+'px!important');
          }

          if (cell && !cell.generated) {
            ctrl.data = cell;
          }

          ctrl.gridElement = {
            rowIndex,
            rowKey: key,
            cellIndex,
            rowId: row.id,
            HTMLElement: tdEl,
            data: control.data,
            record: control.data[rowIndex],
            grid: control,
          };
          ctrl.lookAndFeel = merge({}, gridOptions.lookAndFeel, ctrl.lookAndFeel);
          options.controls[key + '-' + cellIndex] = {...ctrl, gridElement:{...ctrl.gridElement,grid:null}};
          control.controls[key + '-' + cellIndex] = ctrl;
          controlIndex++;
        });
        while (rowEl.childNodes.length > gridOptions.columns.length) {
          rowEl.lastChild.remove();
        }
      });

      while (tableEl.childNodes.length - startIndex > control.data.length) {
        tableEl.lastChild.remove();
      }

      each(control.controls, (ctrl, i) => {
        if (!options.controls[i]) {
          options.controls[i] = null;
          delete control.controls[i];
        }
      });

      // console.timeEnd('grid render rows');
    } else {
      options.controls = control.controls;
    }

    const nodes = Array.from(tableEl.childNodes.values());
    // console.time('grid render row controls');
    renderControls(instance, path,control.nicePath, (ctrl) => {
      if (ctrl) {
        const rowEl = nodes.find((n) => n.dataset.rowKey == ctrl.options.gridElement.rowKey);
        return rowEl && rowEl.childNodes[ctrl.options.gridElement.cellIndex];
      }
    });
    // console.timeEnd('grid render row controls');

    let value =
      (control.options.selectionMode &&
        control.options.selectionMode !== 'none' &&
        control.options.selectionMode !== 'all' &&
        control.value) ||
      [];
    let selectionHash = funJSON.stringify(value);

    // console.log({ selectionHash });
    // console.log(control);
    if (control.selectionHash !== selectionHash) {
      // console.time('grid render selected css');
      control.selectionHash = selectionHash;
      if (!control._oldValue) {
        control._oldValue = [];
      }

      if (control.options.idForceUnique) {
        for (let ri = 0; ri < tableEl.childNodes.length; ri++) {
          // const rowEl = tableEl.childNodes[ri];
          const rowEl = control._rowElements[ri] || tableEl.childNodes[ri];
          if (value.indexOf(rowEl.dataset.rowId) == -1) {
            rowEl.classList.remove('selected');
          } else {
            rowEl.classList.add('selected');
          }
        }
      } else {
        const c = tableEl.childNodes.length;
        for (let ri = 0; ri < c; ri++) {
          // const rowEl = tableEl.childNodes[ri];
          const rowEl = control._rowElements[ri] || tableEl.childNodes[ri];
          const i = rowEl.dataset.rowIndex;
          if (i == 'header') {
            continue;
          }

          if (value.indexOf(Number(i)) == -1) {
            rowEl.classList.remove('selected');
          } else {
            rowEl.classList.add('selected');
          }
        }
      }

      // console.timeEnd('grid render selected css');

      // console.time('grid render synced checkboxes');
      each(instance.controls, (ctrl) => {
        if (ctrl.gridElement && ctrl.gridSelectionSync && ctrl.parentControl === control) {
          let isChecked;
          if (control.options.idForceUnique) {
            isChecked = (control.value || []).indexOf(ctrl.gridElement.rowId) !== -1;
          } else {
            isChecked = (control.value || []).indexOf(ctrl.gridElement.rowIndex) !== -1;
          }

          if (isChecked !== ctrl.isChecked) {
            ctrl.isChecked = isChecked;
            ctrl.render();
          }
        }
      });
      // console.timeEnd('grid render synced checkboxes');
    }

    // console.timeEnd('grid render');

    return control.el;
  }
}
